import React, {
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useMutation,
} from '@tanstack/react-query';
import {
  EncounterVitalsRequestDTO,
} from 'dtos';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import clsx from 'clsx';
import EditIcon from 'core/icons/edit';
import Skeleton from 'core/components/Skeleton';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import IconButton from 'core/components/IconButton';
import {
  Button,
} from '@populateui/ui';
import {
  useNotify,
} from 'core/hooks';
import {
  patientEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  postApiEncounterSaveVitals,
} from 'endpoints';
import useGetVitals from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/Vitals/hooks';
import vitalsConfig from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/Vitals/vitalsConfig';
import VitalsFieldRow from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/Vitals/VitalsFieldRow';
import {
  VitalsFormValues,
  VitalsData,
} from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/Vitals/types';
import {
  normalizeVitalsFormValues,
  formatVitalDisplayValue,
  serializeVitals,
} from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/Vitals/utils';
import {
  formatDateTime,
  formatTime,
} from 'utils/date';
import dayjs from 'utils/dayjs';
import {
  VITALS_EMPTY_STATE_TEXT,
} from 'core/vitals/utils';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';

type Props = {
  patientId: number;
  encounterId: number;
  readOnly?: boolean;
};

function getUpdatedText(vitals?: VitalsData | null): string {
  const updatedAt = vitals?.updatedOn ?? vitals?.createdOn;

  if (!updatedAt || !vitals) {
    return VITALS_EMPTY_STATE_TEXT.noRecordedVitals;
  }

  const updatedAtText = dayjs(updatedAt).isSame(dayjs(), 'day')
    ? `today, ${formatTime(updatedAt)}`
    : formatDateTime(updatedAt);

  return `Updated ${updatedAtText} by ${vitals.updatedByUserName?.indexByLastNameWithCredentials ?? DEFAULT_EMPTY_VALUE}`;
}

export default function VitalsSection({
  patientId,
  encounterId,
  readOnly = false,
}: Props) {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const sectionRef = useRef<HTMLDivElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const notify = useNotify();
  const {
    data: vitals,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetVitals(encounterId);
  const {
    mutateAsync: saveVitals,
    isPending: isSaving,
  } = useMutation({
    mutationFn: (payload: EncounterVitalsRequestDTO) => postApiEncounterSaveVitals(payload),
  });

  const formProps = useForm<VitalsFormValues>({
    mode: 'onChange',
    defaultValues: normalizeVitalsFormValues(vitals),
  });

  const {
    reset,
    getValues,
  } = formProps;
  const isEditing = !readOnly && mode === 'edit';
  const hasRecordedVitals = Boolean(vitals?.updatedOn ?? vitals?.createdOn);
  const shouldShowVitalsFields = isEditing || hasRecordedVitals;
  const auditLine = isLoading
    ? <Skeleton className="h-4 !w-40" />
    : <span className="text-xs text-gray-500">{getUpdatedText(vitals)}</span>;
  const emptyStatus = !isLoading && !hasRecordedVitals && !isEditing
    ? <span className="text-xs text-gray-500">{VITALS_EMPTY_STATE_TEXT.noRecordedVitals}</span>
    : null;

  const handleEdit = () => {
    reset(normalizeVitalsFormValues(vitals));
    setMode('edit');
  };

  useEffect(() => {
    if (isEditing) {
      sectionRef.current?.querySelector<HTMLInputElement>('input:not([disabled])')?.focus();
    }
  }, [isEditing]);

  const handleInputKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || !isEditing || !(event.target instanceof HTMLInputElement)) {
      return;
    }

    event.preventDefault();
    const inputs = Array.from(sectionRef.current?.querySelectorAll<HTMLInputElement>('input:not([disabled])') ?? []);
    const nextInput = inputs[inputs.indexOf(event.target) + 1];

    if (nextInput) {
      nextInput.focus();
    } else {
      saveButtonRef.current?.focus();
    }
  };

  const handleCancel = () => {
    reset(normalizeVitalsFormValues(vitals));
    setMode('view');
  };

  const handleSave = async () => {
    if (!isEditing) {
      return;
    }

    try {
      await saveVitals(serializeVitals(encounterId, getValues()));
      notify({
        variant: 'success',
        message: 'Vitals saved successfully!',
      });
      await refetch();
      setMode('view');
    } catch {
      // The global mutation error handler reports the error and the editor remains open.
    }
  };

  const handleOpenTrends = () => {
    patientEventChannel.emit('onOpenVitalsTrends', {
      patientId,
    });
  };
  const vitalsFields = shouldShowVitalsFields ? (
    <ul className="list-disc list-outside pl-4 !break-words">
      {vitalsConfig.map((config) => (
        <VitalsFieldRow
          key={config.id}
          config={config}
          isEditing={isEditing}
          values={Object.fromEntries(config.fields.map(({ key }) => [
            key,
            formatVitalDisplayValue(vitals?.[key], key),
          ]))}
        />
      ))}
    </ul>
  ) : null;

  return (
    <FormProvider {...formProps}>
      <div ref={sectionRef} className="flex flex-col text-xs" onKeyDownCapture={handleInputKeyDown}>
        <div className="flex min-h-6 items-center gap-1">
          <h4 className="text-xs font-semibold">Vitals</h4>
          <div className="flex items-center gap-0.5">
            <IconButton
              tooltip="View vitals trends"
              size="small"
              onClick={handleOpenTrends}
              color="info"
              aria-label="View vitals trends"
              disabled={patientId <= 0}
            >
              <ShowChartRoundedIcon className="!w-5 !h-5 !text-inherit" />
            </IconButton>
            {isError ? (
              <IconButton
                tooltip="Retry loading vitals"
                size="small"
                onClick={() => { refetch(); }}
                color="info"
                aria-label="Retry loading vitals"
                disabled={isFetching}
              >
                <RefreshRoundedIcon className="!h-5 !w-5 !text-inherit" />
              </IconButton>
            ) : !readOnly && !isLoading && (
              <IconButton
                tooltip="Edit Vitals"
                size="small"
                onClick={handleEdit}
                color="info"
                aria-label="Edit Vitals"
                disabled={isEditing}
              >
                <EditIcon className="!h-5 !w-5 !text-inherit" />
              </IconButton>
            )}
          </div>
        </div>
        {emptyStatus && <div className="text-xs text-gray-500">{emptyStatus}</div>}
        <div className={clsx('transition-opacity duration-200 ease-in-out', {
          'min-h-[4.5rem]': isLoading || shouldShowVitalsFields,
        })}>
          {isLoading ? (
            <div className="flex flex-col gap-1">
              {vitalsConfig.map((config) => (
                <Skeleton key={config.id} className="h-6 !w-full" />
              ))}
            </div>
          ) : vitalsFields}
          {(isEditing || hasRecordedVitals) && (
          <div className="mt-1 flex min-h-8 w-full items-center">
            {isEditing ? (
              <div className="flex min-h-8 w-full items-center justify-end gap-2">
                <Button
                  className="!border-secondary !px-2.5 !py-0.5 disabled:!border-gray-300"
                  variant="outlined"
                  color="primary"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  ref={saveButtonRef}
                  className="!border-secondary !px-2.5 !py-0.5 disabled:!border-gray-300"
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.currentTarget.click();
                    }
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              hasRecordedVitals && auditLine
            )}
          </div>
          )}
        </div>
      </div>
    </FormProvider>
  );
}
